import {
  useState,
  useEffect,
  useParams,
  useNavigate,
  useDispatch,
  titleToUrl,
} from '../../imports/generalImport';
import { Box, Container } from '../../imports/muiImport';
import {
  createDenominationData,
  fetchProductById,
  isDenominationAvailable,
  isProductLive,
  validateOutOfStock,
} from './productDetailHook';

// components
import PageBreadcrumb from '../../components/other/PageBreadcrumb';
import GiftCardSectionSkeleton from './GiftCardSectionSkeleton';
import ProductDetailCard from './productUtils/ProductDetailCard';
import ProductDetailTabView from './productUtils/ProductDetailTabView';
import { setProductDetails, wipeOutProductDetails } from '../../store/productInfo/action';
import { setNewDenomination, wipeOutDenomination } from '../../store/productDetails/action';
import { Helmet } from 'react-helmet-async';
import ThreeGiftCard from '../DASHBOARD/ThreeGiftCard';
import PageNotFound from '../OTHERPAGE/PageNotFound';
import { isDiscountedProduct } from '../../hooks/utilsFn/utilsFn';
import { addPost, resetPosts, setSelectedPost } from '../../store/giftcardDesign/action';
import YouMightBeInterested from './YouMightBeInterested';

export const productDetailHandler = async ({
  urlId,
  urlTitle,
  dispatch,
  setProductFound,
  setProductDetail,
  setPageLoading,
  setOutOfStock,
  setShowRangeDenomination,
  isMobile = false
}) => {
  let result = { success: false, data: null, error: '' };

  try {
    let actualData = await fetchProductById(urlId);
    let mainData = {};

    const validateProductTitleResponse = validateProductTitle(actualData?.data, urlTitle, setProductFound);
    const { productType, discount } = isDiscountedProduct(
      actualData?.data?.discount,
      actualData?.data?.cashback_amount
    );

    if (actualData?.status && validateProductTitleResponse) {
      mainData = actualData?.data;
      mainData.isShowUniqueDiscount = false;

      if (setProductDetail) {
        setProductDetail(mainData);
      }

      mainData.productType = productType;
      mainData.grossAmount = 0;
      mainData.discount = discount;
      mainData.originalDiscount = discount;
      mainData.transactionMSG = actualData?.data?.limit_transaction_text || '';
      mainData.transactionLimit = actualData?.data?.limit_transaction_amount || 10000;
      mainData.isCashbackProduct = productType === 'Cashback';
      mainData.payment_methods = actualData?.data?.payment_methods?.map((item) => {
        const discountValue = parseFloat(
          (mainData?.originalDiscount - item?.percentage).toFixed(2)
        );

        const finalDiscountValue = discountValue > 0 ? discountValue : 0;

        return {
          ...item,
          showValue: finalDiscountValue,
          originalDiscount: mainData?.originalDiscount,
        }
      });

      mainData.isPaymentMethodEmpty = typeof actualData?.data?.payment_methods === 'undefined' ?
        true :
        Array.isArray(actualData?.data?.payment_methods) && actualData?.data?.payment_methods?.length === 0;
      mainData.isAnyPaymentGatewayActive = mainData.isPaymentMethodEmpty ? false : actualData?.data?.payment_methods?.filter((item) => item?.isActive)?.length > 0 ? true : false;
      mainData.isNewPaymentMethodsActive = !mainData?.isPaymentMethodEmpty;
      mainData.isOldPayUActive = typeof actualData?.data?.payment_methods === 'undefined' ? true : false;

      // Task 1: Check Product is InStock or OutOf Stock
      const isProductLiveStatus = isProductLive(mainData.front);
      mainData.outOfStock = isProductLiveStatus;

      const isRange = actualData?.data.isRange;
      const front = actualData?.data.front;
      const denom = actualData?.data?.denomination;
      const validateResponse = validateOutOfStock(isRange, front, denom);

      let isOutOfStock = isProductLiveStatus;
      let isRangeDenomination = false;

      if (validateResponse.outOfStockStatus) {
        mainData.outOfStock = true;
        isOutOfStock = true;
      }

      if (validateResponse.rangeDenominationStatus) {
        mainData.rangeDenomination = true;
        isRangeDenomination = true;
      }

      // Task 2: Check product denomination availability
      const isDenoAvailable = isDenominationAvailable(mainData?.['denomination']);

      if (isDenoAvailable?.isDenominationAvailableStatus) {
        mainData.rangeDenomination = false;
        isRangeDenomination = false;

        const newDeno = createDenominationData(mainData, mainData.isShowUniqueDiscount);
        dispatch(setNewDenomination(newDeno));
      } else {
        mainData.rangeDenomination = true;
        isRangeDenomination = true;
      }

      // Update mobile-specific states if provided
      if (isMobile) {
        if (setOutOfStock) setOutOfStock(isOutOfStock);
        if (setShowRangeDenomination) setShowRangeDenomination(isRangeDenomination);
      }

      // Dispatch to Redux
      dispatch(setProductDetails(mainData));

      // Gift card design templates handling
      if (mainData?.isShowGiftDesignOption) {
        const predefinedTemplates = mainData?.predesignedTemplates || [];
        if (predefinedTemplates?.length > 0) {
          predefinedTemplates?.forEach((element) => {
            const post = {
              id: element?.id,
              image: element?.img,
              altText: element?.altText || `Gift Cover ${element?.id}`,
              title: element?.title || '',
              isTemplate: element?.isTemplate || true,
              isDeleteable: element?.isDeleteable || false,
              isDefault: element?.isDefault || false,
              isSelected: element?.isSelected || false,
            };
            dispatch(addPost(post));
          });

          const findDefault = predefinedTemplates?.find((item) => item.isDefault === true);
          const defaultSelectedPost = {
            id: findDefault?.id,
            image: findDefault?.image,
            altText: findDefault?.altText || `Gift Cover ${findDefault?.id}`,
            title: findDefault?.title || '',
            isTemplate: findDefault?.isTemplate || true,
            isDeleteable: findDefault?.isDeleteable || false,
            isDefault: findDefault?.isDefault || false,
            isSelected: findDefault?.isSelected || false,
          };

          dispatch(setSelectedPost(defaultSelectedPost || predefinedTemplates[0]));
        }
      }

      result = { success: true, data: mainData };
    } else {
      if (setProductFound) setProductFound(true);
      result = { success: false, error: 'Product not found' };
    }
  } catch (error) {
    console.error(error);
    console.error('something Went Wrong !');
    if (setProductFound) setProductFound(true);
    result = { success: false, error: error.message };
  } finally {
    // ✅ This will always execute after try/catch completes
    if (setPageLoading) setPageLoading(false);
  }

  return result;
};


function validateProductTitle(actualData, urlTitle, setProductFound) {
  const userGivenTitle = urlTitle?.toLowerCase();
  const ourProductTitle = actualData?.title?.toLowerCase();
  const ourFinalTitle = titleToUrl(ourProductTitle);

  if (ourFinalTitle === userGivenTitle) {
    if (setProductFound) setProductFound(false);
    return true;
  } else {
    if (setProductFound) setProductFound(true);
    return true;
  }
}

function ProductDetail() {
  // States Initialize
  const [cpId, setCPId] = useState(0);
  const [productDetail, setProductDetail] = useState([]);
  const [productFound, setProductFound] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  let { title: urlTitle, id: urlId } = useParams();
  const canonicalUrl = `https://www.99gift.in/card-details/${urlId}/${titleToUrl(urlTitle)}`;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (urlId != cpId) {
      dispatch(wipeOutProductDetails());
      dispatch(wipeOutDenomination());
      dispatch(resetPosts());
      setPageLoading(() => true);
      productDetailHandler({
        urlId,
        urlTitle,
        dispatch,
        setProductFound,
        setProductDetail,
        setPageLoading,
        isMobile: false
      });
      setCPId(urlId);
    }
  }, [urlId, urlTitle]);

  if (productFound) {
    return <PageNotFound />;
  }

  return (
    <>
      <Helmet>
        <title>
          {productDetail?.['metaSEO']?.['metaTitle']
            ? productDetail?.['metaSEO']?.['metaTitle']
            : `Buy ${urlTitle} - 99gift.in`}
        </title>

        <meta
          property="og:title"
          content={
            productDetail?.['metaSEO']?.['metaOGTitle']
              ? productDetail?.['metaSEO']?.['metaOGTitle']
              : `Buy ${urlTitle} - 99gift.in`
          }
        />

        <meta
          name="description"
          content={
            productDetail?.['metaSEO']?.['metaDescription']
              ? productDetail?.['metaSEO']?.['metaDescription']
              : `Get instant discounts on ${urlTitle} when you purchase them online through 99gift. Take advantage of great deals on ${urlTitle}, coupons, and promo codes.`
          }
        />

        <meta
          name="og:description"
          content={
            productDetail?.['metaSEO']?.['metaOGDescription']
              ? productDetail?.['metaSEO']?.['metaOGDescription']
              : `Get instant discounts on ${urlTitle} when you purchase them online through 99gift. Take advantage of great deals on ${urlTitle}, coupons, and promo codes.`
          }
        />

        <meta property="og:url" content={canonicalUrl} />
        <link rel="canonical" href={canonicalUrl} />

        {productDetail?.['metaSchema'] && (
          <script type="application/ld+json">{productDetail?.['metaSchema']}</script>
        )}

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={productDetail?.['metaSEO']?.['metaTitle']} />
        <meta
          name="twitter:description"
          content={productDetail?.['metaSEO']?.['metaDescription']}
        />
        <meta name="twitter:image" content={productDetail?.image} />
        <meta name="twitter:url" content={canonicalUrl} />
      </Helmet>

      <Container maxWidth="xl">
        <PageBreadcrumb
          home="Home"
          category={productDetail?.category?.title}
          currentProduct={productDetail?.title}
        />

        <Box
          sx={{
            backgroundColor: 'transparent',
            backgroundAttachment: 'fixed',
          }}
        >
          <ProductDetailCard pageLoading={pageLoading} />
          <ProductDetailTabView pageLoading={pageLoading} />
        </Box>

<Box sx={{ pb: 4,pt: 4 }}>
  {pageLoading ? (
    <GiftCardSectionSkeleton />
  ) : (
    <YouMightBeInterested
      pageLoading={false}          // 👈 false bhejo, warna apna skeleton dikhayega
      giftCardData={productDetail.like}
    />
  )}
</Box>      </Container>
    </>
  );
}

export default ProductDetail;
